import { useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Check, ChevronsUpDown } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { LoadingSpinner } from "@/components/ui/loading-spinner";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useCampaign } from "@/contexts/CampaignContext";
import { cn } from "@/lib/utils";

interface EmailLog {
  id: string;
  subscriber_id: string;
  day_number: number;
  postmark_message_id: string | null;
  status: "sent" | "failed" | "bounced";
  error_text: string | null;
  sent_at: string;
  subscriber: {
    email: string;
  };
  campaign_day: {
    title: string;
  } | null;
}

export default function EmailLogs() {
  console.log('EmailLogs component rendered');
  const { selectedCampaignId } = useCampaign();
  const [searchParams, setSearchParams] = useSearchParams();
  const [comboboxOpen, setComboboxOpen] = useState(false);

  // Get filters from URL
  const search = searchParams.get("search") || "";
  const statusFilter = searchParams.get("status") || "all";

  const dateFormatter = useMemo(() => new Intl.DateTimeFormat(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  }), []);

  // Fetch all subscriber emails for the campaign
  const { data: subscriberEmails = [] } = useQuery({
    queryKey: ['subscriber-emails', selectedCampaignId],
    enabled: !!selectedCampaignId,
    queryFn: async () => {
      if (!selectedCampaignId) return [];

      const { data, error } = await supabase
        .from("subscribers")
        .select("email")
        .eq("campaign_id", selectedCampaignId)
        .order("email");

      if (error) {
        console.error("Error loading subscriber emails:", error);
        return [];
      }

      return data.map(s => s.email);
    },
  });

  const { data: emailLogs = [], isLoading } = useQuery({
    queryKey: ['email-logs', selectedCampaignId, statusFilter, search],
    enabled: !!selectedCampaignId,
    queryFn: async () => {
      if (!selectedCampaignId) return [];

      // Get campaign_days for this campaign to join manually
      const { data: campaignDays } = await supabase
        .from("campaign_days")
        .select("day_number, title")
        .eq("campaign_id", selectedCampaignId);

      const campaignDaysMap = new Map(
        campaignDays?.map(d => [d.day_number, d.title]) || []
      );

      // Build query for email logs with server-side filtering
      let query = supabase
        .from("email_logs")
        .select(`
          id,
          subscriber_id,
          day_number,
          postmark_message_id,
          status,
          error_text,
          sent_at,
          subscriber:subscribers!inner(email, campaign_id)
        `)
        .eq("subscriber.campaign_id", selectedCampaignId)
        .order("sent_at", { ascending: false })
        .limit(500); // Limit to most recent 500 logs

      // Apply status filter if not "all"
      if (statusFilter !== "all") {
        query = query.eq("status", statusFilter);
      }

      // Apply email search filter server-side
      if (search) {
        query = query.ilike("subscriber.email", `%${search}%`);
      }

      const { data, error } = await query;

      if (error) {
        console.error("Error loading email logs:", error);
        return [];
      }

      // Manually join campaign_day titles
      return (data || []).map(log => ({
        ...log,
        campaign_day: {
          title: campaignDaysMap.get(log.day_number) || null
        }
      })) as unknown as EmailLog[];
    },
  });

  const handleSearchChange = (value: string) => {
    const newParams = new URLSearchParams(searchParams);
    if (value) {
      newParams.set("search", value);
    } else {
      newParams.delete("search");
    }
    setSearchParams(newParams);
  };

  const handleStatusChange = (value: string) => {
    const newParams = new URLSearchParams(searchParams);
    if (value === "all") {
      newParams.delete("status");
    } else {
      newParams.set("status", value);
    }
    setSearchParams(newParams);
  };

  const handleClearFilters = () => {
    setSearchParams({});
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case "sent":
        return "bg-success";
      case "failed":
        return "bg-destructive";
      case "bounced":
        return "bg-warning";
      default:
        return "bg-muted";
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl md:text-3xl font-bold mb-4">Email Logs</h1>

        <div className="flex flex-col sm:flex-row gap-4 mb-4">
          <div className="flex-1">
            <Label htmlFor="search" className="sr-only">Filter by email</Label>
            <Popover open={comboboxOpen} onOpenChange={setComboboxOpen}>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  role="combobox"
                  aria-expanded={comboboxOpen}
                  className="w-full justify-between"
                >
                  {search || "Filter by email..."}
                  <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-[var(--radix-popover-trigger-width)] p-0" align="start">
                <Command>
                  <CommandInput placeholder="Search emails..." />
                  <CommandList>
                    <CommandEmpty>No email found.</CommandEmpty>
                    <CommandGroup>
                      {subscriberEmails.map((email) => (
                        <CommandItem
                          key={email}
                          value={email}
                          onSelect={(currentValue) => {
                            handleSearchChange(currentValue === search ? "" : currentValue);
                            setComboboxOpen(false);
                          }}
                        >
                          <Check
                            className={cn(
                              "mr-2 h-4 w-4",
                              search === email ? "opacity-100" : "opacity-0"
                            )}
                          />
                          {email}
                        </CommandItem>
                      ))}
                    </CommandGroup>
                  </CommandList>
                </Command>
              </PopoverContent>
            </Popover>
          </div>

          <div className="w-full sm:w-48">
            <Label htmlFor="status" className="sr-only">Filter by status</Label>
            <Select value={statusFilter} onValueChange={handleStatusChange}>
              <SelectTrigger id="status">
                <SelectValue placeholder="Filter by status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="sent">Sent</SelectItem>
                <SelectItem value="failed">Failed</SelectItem>
                <SelectItem value="bounced">Bounced</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {(search || statusFilter !== "all") && (
            <Button variant="outline" onClick={handleClearFilters}>
              Clear Filters
            </Button>
          )}
        </div>

        <p className="text-sm text-muted-foreground">
          {isLoading ? "Loading..." : `Showing ${emailLogs.length} log${emailLogs.length !== 1 ? "s" : ""} (most recent 500)`}
        </p>
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <LoadingSpinner />
        </div>
      ) : (
        <div className="grid gap-4">
          {emailLogs.length === 0 ? (
            <Card>
              <CardContent className="py-8 text-center text-muted-foreground">
                No email logs found for the selected filters.
              </CardContent>
            </Card>
          ) : (
            emailLogs.map((log) => (
            <Card key={log.id}>
              <CardHeader>
                <CardTitle className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                  <Link
                    to="/admin/subscribers"
                    className="text-base font-medium hover:underline"
                  >
                    {log.subscriber.email}
                  </Link>
                  <Badge className={`${getStatusColor(log.status)} self-start sm:self-auto`}>
                    {log.status}
                  </Badge>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-muted-foreground">Day:</span>{" "}
                    <span className="font-medium">
                      {log.day_number} - {log.campaign_day?.title || "Unknown"}
                    </span>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Sent At:</span>{" "}
                    <span className="font-medium">
                      {dateFormatter.format(new Date(log.sent_at))}
                    </span>
                  </div>
                  {log.postmark_message_id && (
                    <div className="sm:col-span-2">
                      <span className="text-muted-foreground">Message ID:</span>{" "}
                      <span className="font-mono text-xs">{log.postmark_message_id}</span>
                    </div>
                  )}
                  {log.error_text && (
                    <div className="sm:col-span-2">
                      <span className="text-muted-foreground">Error:</span>{" "}
                      <span className="font-medium text-destructive">{log.error_text}</span>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
            ))
          )}
        </div>
      )}
    </div>
  );
}
